import { AxiosError } from 'axios';
import { pipe } from 'fp-ts/function';
import { log } from 'fp-ts/lib/Console';
import * as E from 'fp-ts/lib/Either';
import * as IO from 'fp-ts/lib/IO';
import * as O from 'fp-ts/lib/Option';
import * as T from 'fp-ts/lib/Task';
import * as TE from 'fp-ts/lib/TaskEither';
import moment from 'moment';
import {
  constantDelay,
  limitRetries,
  monoidRetryPolicy,
  RetryPolicy,
  RetryStatus,
} from 'retry-ts';
import { retrying } from 'retry-ts/lib/Task';

import { AdditionalInfo, DrupalResponse } from './typings';
import { transformUserInfo } from './utils';
import * as Drupal from '../utils/dataFetching/drupalFetcher';
import * as GatsbyApp from '../utils/dataFetching/gatsbyFetcher';

import { Auth0IDToken } from '@domain/auth';

import { AddLog } from '@hooks/logger';

import { DIS } from '@typings/dis';
import {
  ClientServerDate,
  Status,
  UserAuthenticate,
} from '@typings/express-responses';
import { LogLevel } from '@typings/operations';

// See comment on #63 line
const maxAttempts = 5;
const delayMiliseconds = 3000;
const policy = monoidRetryPolicy.concat(
  constantDelay(delayMiliseconds),
  limitRetries(maxAttempts)
);

// idTokenFormData :: idToken -> FormData
export const idTokenFormData = (
  idToken: string,
  basicUserInfo: DIS.SSOTicketResponse | null,
  addLog: AddLog
): FormData => {
  const userInfo = JSON.stringify(transformUserInfo(basicUserInfo));
  const bodyFormData = new FormData();
  bodyFormData.append('idToken', idToken);
  bodyFormData.append('basicUserInfo', userInfo);

  addLog({
    level: LogLevel.Info,
    message: `BasicUserInfo: ${userInfo}`,
    other: JSON.stringify(transformUserInfo(basicUserInfo)),
  });
  return bodyFormData;
};

const fetcher = (
  language: string,
  idToken: string,
  addLog: AddLog,
  { initialCall, email, id }: AdditionalInfo,
  basicUserInfo: DIS.SSOTicketResponse | null
) =>
  pipe(
    Drupal.customCall({
      url: `${language}/restapi/okta/authenticate`,
      method: 'POST',
      data: idTokenFormData(idToken, basicUserInfo, addLog),
      headers: { 'Content-Type': 'multipart/form-data' },
      withCredentials: true,
    }),
    IO.map(
      T.map(data => {
        addLog({
          level: LogLevel.Info,
          message: `authenticateUserBackendApp (${initialCall}): email: ${email}, id: ${id}`,
          other: JSON.stringify(data),
        });
        return data;
      })
    )
  );

const logDelay = (status: RetryStatus) =>
  TE.rightIO(
    log(
      pipe(
        status.previousDelay,
        O.map(delay => `retrying in ${delay} milliseconds...`),
        O.getOrElse(() => '')
      )
    )
  );

// When we create a new DIS individual, it takes some time for the individual
// to be synced from DIS3 to the RotaryOkta service. Drupal authentication
// does not work until this is done. According to Steve Cohn, it should be
// enough to retry 5 times with 3 second interval.

export const authenticateUserBackendApp = (
  lang: string,
  id: string,
  addLog: AddLog,
  additionalInfo: AdditionalInfo,
  basicUserInfo: DIS.SSOTicketResponse | null
): T.Task<DrupalResponse> =>
  pipe(
    retrying(
      policy,
      status =>
        pipe(
          logDelay(status),
          TE.apSecond(
            fetcher(lang, id, addLog, additionalInfo, basicUserInfo)()
          )
        ),
      E.isLeft
    ),
    TE.fold(
      (e: AxiosError) =>
        T.of({
          error_code: e.code || '-1',
          error_message: e.message || e.toString(),
        }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (data: any) => T.of(data?.success === 1 ? Status.OK : data)
    )
  );

type UserAuthenticateData = {
  accessToken: string;
  idToken: Auth0IDToken | string;
  date: string;
};

export const callAuthenticateAPI = (
  accessToken: string,
  idToken: Auth0IDToken | string
) =>
  pipe(
    GatsbyApp.post<UserAuthenticateData, UserAuthenticate>(
      'user/authenticate',
      {
        accessToken,
        idToken,
        date: moment().toISOString(),
      }
    )
  );

const maxTimeoutPolicy = (maxTimeout: number): RetryPolicy => {
  const timeToStop = Date.now() + maxTimeout;
  return () =>
    Date.now() >= timeToStop || maxTimeout === 0 ? O.none : O.some(0);
};

export const authenticateUserWebApp = (
  accessToken: string,
  idToken: Auth0IDToken | string,
  addLog: AddLog,
  { initialCall, email, id }: AdditionalInfo
) =>
  pipe(
    retrying<Error | UserAuthenticate>(
      maxTimeoutPolicy(Number(process.env.LOGIN_RETRY_TIMEOUT)),
      callAuthenticateAPI(accessToken, idToken),
      data => !('status' in data) || data.status === Status.OKTA_ERROR
    ),
    T.map(data => {
      addLog({
        level: LogLevel.Info,
        message: `authenticateUserWebApp (${initialCall}): email: ${email}, id: ${id}`,
        other: JSON.stringify(data),
      });
      return data;
    })
  );

export const checkServerDate = (
  date: string
): IO.IO<T.Task<ClientServerDate | Error>> =>
  GatsbyApp.post<{ date: string }, ClientServerDate>('client-server-date', {
    date,
  });
